import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/hooks/use-toast";
import { useRef, useState, useEffect } from "react";
import ChatInterface, { ChatInterfaceRef } from "@/components/ChatInterface";
import RecordsPanel, { type RecordsSectionId } from "@/components/records/RecordsPanel";
import { CareTeamQuestions } from "@/components/CareTeamQuestions";
import ConversationSidebar from "@/components/ConversationSidebar";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useTranslation } from "react-i18next";
import i18n, { currentLanguage } from "@/lib/i18n";
import {
  LogOut,
  FileText,
  Stethoscope,
  Bot,
  RefreshCw,
  ClipboardList,
  Maximize2,
  PanelRightOpen,
} from "lucide-react";

type MainView = 'chat' | 'records' | 'questions';

/**
 * Calculate patient age in a human-readable format (e.g., "3 months", "2 years", "15 years")
 */
export function calculateAge(dateOfBirth: string): string {
  // Parse as local date to avoid UTC timezone shift (FHIR dates are "YYYY-MM-DD")
  const [y, m, d] = dateOfBirth.split('-').map(Number);
  const dob = new Date(y, m - 1, d);
  const now = new Date();

  let years = now.getFullYear() - dob.getFullYear();
  let months = now.getMonth() - dob.getMonth();
  if (now.getDate() < dob.getDate()) months--;
  if (months < 0) { years--; months += 12; }

  if (years >= 2) return `${years} years`;
  const totalMonths = years * 12 + months;
  if (totalMonths >= 1) return `${totalMonths} months`;
  const days = Math.floor((now.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24));
  return `${days} days`;
}

/**
 * Format the last sync time for display
 * Same day: "Xm ago" or "Xh ago"
 * Previous days: "MMM D, h:mm A"
 */
function formatLastSyncTime(lastSyncAt: string | null | undefined): string {
  if (!lastSyncAt) return i18n.t('dashboard.syncNever');

  const syncDate = new Date(lastSyncAt);
  const now = new Date();

  // Check if same day
  const isSameDay = syncDate.toDateString() === now.toDateString();

  if (isSameDay) {
    const diffMs = now.getTime() - syncDate.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 1) return i18n.t('dashboard.syncJustNow');
    if (diffMins < 60) return i18n.t('dashboard.syncMinutesAgo', { count: diffMins });
    return i18n.t('dashboard.syncHoursAgo', { count: diffHours });
  }

  // Different day - show date and time
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium', timeStyle: 'short' }).format(syncDate);
}

export default function Dashboard() {
  const { t, i18n: i18nInstance } = useTranslation();
  const { logout } = useAuth();
  const { toast } = useToast();
  const chatRef = useRef<ChatInterfaceRef>(null);
  const queryClient = useQueryClient();
  const [isParsingData, setIsParsingData] = useState(false);

  // Navigation state - track which view is main (75%) with Chat always in right rail (25%)
  const [mainView, setMainView] = useState<MainView>('chat'); // Default: Chat main, Medical Records in right rail
  const [rightRailWidth, setRightRailWidth] = useState(35); // Default 65/35 split

  // Track what content is shown in right rail for each main view scenario
  const [chatRightRailContent, setChatRightRailContent] = useState<'records' | 'questions'>('records'); // When Chat is main
  const [recordsRightRailContent, setRecordsRightRailContent] = useState<'chat' | 'questions'>('chat'); // When Medical Records is main
  const [questionsRightRailContent, setQuestionsRightRailContent] = useState<'chat' | 'records'>('chat'); // When Care Questions is main

  // Track expand/collapse state for full-screen mode
  const [isExpanded, setIsExpanded] = useState(false);

  // Track conversation sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Track last sync time
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  // Records panel drill-in, lifted so the open section survives switching
  // between the main view, the right rail, and the mobile tabs
  const [openRecordsSection, setOpenRecordsSection] = useState<RecordsSectionId | null>(null);

  // Navigation handler
  const handleViewChange = (view: MainView) => {
    setMainView(view);
  };
  
  // Toggle expand/collapse
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // In a real app, you'd get the patient ID from the user's context
  const patientId = 1;

  const handleAskQuestion = (question: string, displayText?: string) => {
    const isMobile = window.innerWidth < 768; // Tailwind's md breakpoint

    if (isMobile) {
      // On mobile: Always switch to Chat as primary view
      setMainView('chat');
      setChatRightRailContent('records');
    } else {
      // On desktop: Smarter behavior based on current view
      if (mainView === 'records') {
        // Medical Records is main - bring Chat to sidebar
        if (recordsRightRailContent === 'questions') {
          setRecordsRightRailContent('chat'); // Switch from Questions to Chat in sidebar
        }
        // If Chat is already in sidebar, do nothing (keep it there)
      } else if (mainView === 'questions') {
        // Questions is main - switch to Chat main view
        setMainView('chat');
        setChatRightRailContent('records');
      }
      // If Chat is already main, do nothing
    }

    // If view is expanded, collapse it so user can see the chat working
    if (isExpanded) {
      setIsExpanded(false);
    }

    // Send question to chat
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.askQuestion(question, displayText);
      }
    }, 100);
  };

  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ["/api/dashboard", patientId],
    queryFn: () => api.getDashboardData(patientId),
    staleTime: 300000, // Cache for 5 minutes - use Sync button for manual refresh
  });

  // Fetch medical session to get lastSyncAt timestamp
  const { data: medicalSession } = useQuery({
    queryKey: ["/api/medical-session", dashboardData?.session?.id],
    queryFn: async () => {
      if (!dashboardData?.session?.id) return null;
      return api.getMedicalSession(String(dashboardData.session.id));
    },
    enabled: !!dashboardData?.session?.id,
  });

  // Update lastSyncAt when medical session data changes
  useEffect(() => {
    if (medicalSession?.lastSyncAt) {
      setLastSyncAt(medicalSession.lastSyncAt);
    }
  }, [medicalSession]);

  // Set initial sync time from dashboard data if no medical session sync time
  useEffect(() => {
    if (dashboardData?.session?.lastSync && !lastSyncAt) {
      setLastSyncAt(dashboardData.session.lastSync);
    }
  }, [dashboardData, lastSyncAt]);

  // Startup generation: one model call at login for the Patient Summary card,
  // what's-new topics, section overviews, plain-language names, and lab
  // statuses. Never blocks the dashboard — on any error (409 disabled, 502
  // unparseable, 500) the chat home falls back to the current-status summary
  // (below) and the records panel renders the record without AI overviews.
  const startupQuery = useQuery({
    queryKey: ["/api/startup", dashboardData?.patient?.id, i18nInstance.resolvedLanguage],
    queryFn: async () => {
      if (!dashboardData) return null;
      return api.generateStartup(dashboardData, currentLanguage());
    },
    enabled: !!dashboardData,
    staleTime: 300000, // Cache for 5 minutes
    retry: false, // Fall back immediately rather than retrying a disabled/failed call
  });
  const startup = startupQuery.data?.startup;

  // Fallback summary when startup generation is off or failed: the older
  // current-status call, shown in the chat-home card without what's-new topics.
  const fallbackStatusQuery = useQuery({
    queryKey: ["/api/ai-status", dashboardData?.patient?.id, i18nInstance.resolvedLanguage],
    queryFn: async () => {
      if (!dashboardData) return null;
      const response = await fetch('/api/generate-current-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientData: dashboardData, language: currentLanguage() })
      });
      if (!response.ok) throw new Error('Failed to generate status');
      const data = await response.json();
      return data.currentStatus as string;
    },
    enabled: !!dashboardData && startupQuery.isError,
    staleTime: 300000,
    retry: false,
  });
  const fallbackStatus = fallbackStatusQuery.data;
  const chatStartup = startup
    ?? (fallbackStatus ? { summary: fallbackStatus, whatsNew: [], generatedAt: new Date().toISOString(), whatsNewWindow: { kind: 'last24h' } } : undefined);
  // True while the startup call (or, after it fails, the current-status
  // fallback) is still in flight: the chat home shows the new layout with a
  // loading card rather than the old greeting that would later be replaced.
  const startupLoading = !!dashboardData && !chatStartup && (startupQuery.isPending || (startupQuery.isError && fallbackStatusQuery.isPending));

  const handleLogout = () => {
    logout();
    toast({
      title: t('dashboard.logoutToastTitle'),
      description: t('dashboard.logoutToastDescription'),
    });
  };

  const handleParseData = async () => {
    try {
      setIsParsingData(true);
      const response = await fetch('/api/parse-medical-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to parse medical data');
      }

      const result = await response.json();

      // Update the sync timestamp
      if (dashboardData?.session?.id) {
        const updatedSession = await api.updateMedicalSessionSync(String(dashboardData.session.id));
        setLastSyncAt(updatedSession.lastSyncAt || new Date().toISOString());
      }

      // Invalidate and refetch dashboard data with force refresh
      await queryClient.invalidateQueries({ queryKey: ['/api/dashboard', patientId] });
      await queryClient.refetchQueries({ queryKey: ['/api/dashboard', patientId] });

      toast({
        title: t('dashboard.syncSuccessTitle'),
        description: t('dashboard.syncSuccessDescription', { count: Object.values(result.updatedSections).filter(Boolean).length }),
      });
    } catch (error) {
      console.error('Error parsing medical data:', error);
      toast({
        title: t('dashboard.syncErrorTitle'),
        description: t('dashboard.syncErrorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsParsingData(false);
    }
  };


  // Progressive loading text based on elapsed time
  const [loadingStartTime] = useState(() => Date.now());
  const [loadingText, setLoadingText] = useState(t('dashboard.loadingRecords'));

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - loadingStartTime;
      if (elapsed > 30000) {
        setLoadingText(t('dashboard.loadingVerySlow'));
      } else if (elapsed > 10000) {
        setLoadingText(t('dashboard.loadingSlow'));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoading, loadingStartTime, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-medical-bg">
        <header className="bg-white shadow-card sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <Skeleton className="w-10 h-10 rounded-full mr-3" />
                <div>
                  <Skeleton className="w-40 h-6 mb-1" />
                  <Skeleton className="w-32 h-4" />
                </div>
              </div>
              <Skeleton className="w-20 h-8" />
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
            <p className="text-text-secondary text-sm">{loadingText}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-64" />
              <Skeleton className="h-96" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-medical-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-error mb-4">
              <FileText className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-bold text-text-primary mb-2">{t('dashboard.errorTitle')}</h2>
            <p className="text-text-secondary mb-4">
              {t('dashboard.errorDescription')}
            </p>
            <Button onClick={() => window.location.reload()}>
              {t('dashboard.refreshPage')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { patient, session } = dashboardData;

  // Ensure session.id is a string for components that expect string
  const sessionId = String(session.id);

  return (
    <div className="min-h-screen bg-medical-bg">
      
      {/* Header */}
      <header className="bg-white shadow-card sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            {/* Logo - Left */}
            <div className="flex items-center min-w-0 flex-shrink-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 mr-2 sm:mr-3 flex-shrink-0">
                <img src="/litmed_logo.png" alt="LitMed AI" className="w-full h-full object-contain" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-text-primary">LitMed AI</h1>
              </div>
            </div>

            {/* User Info & Logout - Right */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <LanguageToggle />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-text-primary">
                  {patient.firstName} {patient.lastName}
                </p>
                <p className="text-xs text-text-secondary">{t('header.role')}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                aria-label={t('header.logout')}
                title={t('header.logout')}
                className="flex flex-col items-center gap-0.5"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-xs text-gray-500">{t('header.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Top Navigation Menu - Centered with Sync Button */}
      <nav className="bg-white border-b border-gray-200 sticky top-[64px] sm:top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex justify-center flex-1 space-x-8">
            <button
              onClick={() => handleViewChange('chat')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                mainView === 'chat'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="nav-tab-chat"
            >
              <Bot className="h-4 w-4" />
              {t('dashboard.tabChat')}
            </button>
            <button
              onClick={() => handleViewChange('records')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                mainView === 'records'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="nav-tab-medical-records"
            >
              <Stethoscope className="h-4 w-4" />
              {t('dashboard.tabRecords')}
            </button>
            <button
              onClick={() => handleViewChange('questions')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                mainView === 'questions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="nav-tab-care-questions"
            >
              <ClipboardList className="h-4 w-4" />
              {t('dashboard.tabQuestions')}
            </button>
            </div>

            {/* Sync Button - Right side of nav */}
            <div className="flex flex-col items-center gap-1 my-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleParseData}
                disabled={isParsingData}
                className="flex items-center gap-1.5"
                title={t('dashboard.syncTooltip')}
                data-testid="button-sync-header"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isParsingData ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">{isParsingData ? t('dashboard.syncing') : t('dashboard.syncButton')}</span>
              </Button>
              <span className="text-[10px] text-text-secondary whitespace-nowrap">
                {t('dashboard.lastSynced', { time: formatLastSyncTime(lastSyncAt) })}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Container */}
      <div className="flex relative h-[calc(100vh-128px)] sm:h-[calc(100vh-144px)]">

        {/* Left Sidebar - Conversation History */}
        <ConversationSidebar
          sessionId={sessionId}
          patientName={`${patient.firstName} ${patient.lastName}`}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onNewChat={async () => {
            try {
              await api.createNewChat();
              setMainView('chat');
              // Refetch the active conversation (ChatInterface) AND the sidebar
              // list. Without the "/current" key, ChatInterface keeps showing the
              // previous conversation's cached messages, so New Chat looks like a
              // no-op until the component remounts. See onSelectConversation below.
              queryClient.invalidateQueries({ queryKey: ["/api/chat-messages/current"] });
              queryClient.invalidateQueries({ queryKey: ["/api/chat-messages"] });
            } catch (error) {
              console.error('Failed to create new chat:', error);
            }
          }}
          onSelectConversation={async (conversationId: string) => {
            // Load the selected conversation
            try {
              // First, fetch the conversation to set it in the session
              await fetch(`/api/chat-messages/current?conversationId=${conversationId}`, {
                credentials: 'include'
              });

              // Then invalidate queries to reload with the new conversation
              queryClient.invalidateQueries({ queryKey: ["/api/chat-messages/current"] });
              queryClient.invalidateQueries({ queryKey: ["/api/chat-messages"] });
            } catch (error) {
              console.error('Failed to load conversation:', error);
            }
          }}
        />

        {/* Main View - 100% width on mobile, adjustable width on desktop */}
        <div
          className="h-full transition-all duration-300 w-full md:flex-1"
          style={{ width: window.innerWidth >= 768 ? (isExpanded ? '100%' : `${100 - rightRailWidth}%`) : '100%' }}
        >
          {mainView === 'chat' && (
            <div className="h-full bg-white">
              <ChatInterface
                ref={chatRef}
                sessionId={sessionId}
                patientId={session.patientId}
                patientName={`${patient.firstName} ${patient.lastName}`}
                patientData={dashboardData as unknown as Record<string, unknown>}
                startup={chatStartup}
                startupLoading={startupLoading}
                suggestedQuestions={[
                  t('chat.suggestedHospitalStay', { name: patient.firstName }),
                  t('chat.suggestedWarningSigns'),
                  t('chat.suggestedRecoveryHelp', { name: patient.firstName }),
                  t('chat.suggestedBreathing'),
                  t('chat.suggestedLongTermEffects'),
                  t('chat.suggestedActivities')
                ]}
                showRightPanelToggle={!isExpanded}
                rightPanelContent={chatRightRailContent}
                onRightPanelToggle={setChatRightRailContent}
                isExpanded={isExpanded}
                onToggleExpand={toggleExpand}
              />
            </div>
          )}

          {mainView === 'records' && (
            <div className="h-full flex flex-col bg-white relative">
              {/* Expand Button - Top Right Corner - Hidden on mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleExpand}
                className="absolute top-4 right-4 z-10 hidden md:flex"
                title={isExpanded ? t('dashboard.openSidebar') : t('dashboard.expandToFullScreen')}
                aria-label={isExpanded ? t('dashboard.openSidebar') : t('dashboard.expandToFullScreen')}
                data-testid="button-toggle-expand"
              >
                {isExpanded ? <PanelRightOpen className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>

              {/* Records panel: section list with drill-in pages */}
              <RecordsPanel
                data={dashboardData}
                startup={startup ?? null}
                onAskQuestion={handleAskQuestion}
                variant="main"
                openSection={openRecordsSection}
                onOpenSectionChange={setOpenRecordsSection}
              />
            </div>
          )}

          {mainView === 'questions' && (
            <div className="h-full flex flex-col bg-white relative">
              {/* Expand Button - Top Right Corner - Hidden on mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleExpand}
                className="absolute top-4 right-4 z-10 hidden md:flex"
                title={isExpanded ? t('dashboard.openSidebar') : t('dashboard.expandToFullScreen')}
                aria-label={isExpanded ? t('dashboard.openSidebar') : t('dashboard.expandToFullScreen')}
                data-testid="button-toggle-expand-questions"
              >
                {isExpanded ? <PanelRightOpen className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>

              {/* Header - Hero Style */}
              <div className="p-4 sm:p-6 border-b flex-shrink-0">
                <div className="flex items-center gap-2 sm:gap-3 pr-10">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold text-text-primary">{t('dashboard.questionsForCareTeam')}</h2>
                    <p className="text-xs sm:text-sm text-text-secondary mt-1">
                      {t('dashboard.questionsForCareTeamSubtitle')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <CareTeamQuestions
                  sessionId={sessionId}
                  patientId={session.patientId}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Rail - Shows Chat when main is Records/Questions, or Medical Records when main is Chat - Hidden on mobile */}
        {!isExpanded && (
          <div 
            className="relative h-full bg-white border-l flex-col transition-all duration-300 hidden md:flex"
            style={{ width: `${rightRailWidth}%` }}
          >
          {/* Resize Handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize bg-gray-300 hover:bg-primary transition-colors z-40 group"
            onMouseDown={(e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startWidth = rightRailWidth;

              const handleMouseMove = (e: MouseEvent) => {
                const deltaX = startX - e.clientX;
                const windowWidth = window.innerWidth;
                const deltaPercent = (deltaX / windowWidth) * 100;
                const newWidth = Math.max(30, Math.min(50, startWidth + deltaPercent));
                setRightRailWidth(newWidth);
              };

              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };

              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            title={t('dashboard.dragToResize')}
          >
            <div className="absolute inset-y-0 left-0 w-1 bg-transparent group-hover:bg-primary/20" />
          </div>

          {/* Right Rail Content */}
          {mainView === 'chat' ? (
            <div className="h-full flex flex-col">
              {/* Toggle for Chat main view */}
              <div className="p-3 border-b flex-shrink-0">
                <ToggleGroup 
                  type="single" 
                  value={chatRightRailContent}
                  onValueChange={(value) => value && setChatRightRailContent(value as 'records' | 'questions')}
                  className="justify-start gap-1"
                >
                  <ToggleGroupItem value="records" aria-label={t('dashboard.showRecords')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <Stethoscope className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.recordsLabel')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="questions" aria-label={t('dashboard.showQuestions')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.questionsLabel')}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {chatRightRailContent === 'records' ? (
                  <div className="h-full flex flex-col">
                    <div className="p-4 border-b flex-shrink-0">
                      <h3 className="text-sm font-medium text-text-primary">{t('dashboard.medicalRecordsHeading')}</h3>
                    </div>
                    <div className="flex-1 min-h-0">
                      <RecordsPanel
                        data={dashboardData}
                        startup={startup ?? null}
                        onAskQuestion={handleAskQuestion}
                        variant="rail"
                        onOpenFull={() => setMainView('records')}
                        openSection={openRecordsSection}
                        onOpenSectionChange={setOpenRecordsSection}
                      />
                    </div>
                  </div>
              ) : (
                <div className="h-full flex flex-col">
                  <div className="p-4 border-b flex-shrink-0">
                    <h3 className="text-sm font-medium text-text-primary">{t('dashboard.careQuestionsHeading')}</h3>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <CareTeamQuestions
                      sessionId={sessionId}
                      patientId={session.patientId}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : mainView === 'records' ? (
          <div className="h-full flex flex-col">
              {/* Toggle for Medical Records main view */}
              <div className="p-3 border-b flex-shrink-0">
                <ToggleGroup 
                  type="single" 
                  value={recordsRightRailContent}
                  onValueChange={(value) => value && setRecordsRightRailContent(value as 'chat' | 'questions')}
                  className="justify-start gap-1"
                >
                  <ToggleGroupItem value="chat" aria-label={t('dashboard.showChat')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <Bot className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.chatLabel')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="questions" aria-label={t('dashboard.showQuestions')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.questionsLabel')}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {recordsRightRailContent === 'chat' ? (
                  <div className="h-full">
                    <ChatInterface
                      ref={chatRef}
                      sessionId={sessionId}
                      patientId={session.patientId}
                      patientName={`${patient.firstName} ${patient.lastName}`}
                      patientData={dashboardData as unknown as Record<string, unknown>}
                      startup={chatStartup}
                startupLoading={startupLoading}
                      suggestedQuestions={[
                        t('chat.suggestedHospitalStay', { name: patient.firstName }),
                        t('chat.suggestedWarningSigns'),
                        t('chat.suggestedRecoveryHelp', { name: patient.firstName }),
                        t('chat.suggestedBreathing'),
                        t('chat.suggestedLongTermEffects'),
                        t('chat.suggestedActivities')
                      ]}
                    />
                  </div>
                ) : (
                  <div className="h-full flex flex-col">
                    <div className="p-4 border-b flex-shrink-0">
                      <h3 className="text-sm font-medium text-text-primary">{t('dashboard.careQuestionsHeading')}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <CareTeamQuestions
                        sessionId={sessionId}
                        patientId={session.patientId}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {/* Toggle for Care Questions main view */}
              <div className="p-3 border-b flex-shrink-0">
                <ToggleGroup 
                  type="single" 
                  value={questionsRightRailContent}
                  onValueChange={(value) => value && setQuestionsRightRailContent(value as 'chat' | 'records')}
                  className="justify-start gap-1"
                >
                  <ToggleGroupItem value="chat" aria-label={t('dashboard.showChat')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <Bot className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.chatLabel')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="records" aria-label={t('dashboard.showRecords')} className="flex-1 text-xs px-2 py-1.5 data-[state=on]:bg-primary data-[state=on]:text-white">
                    <Stethoscope className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.recordsLabel')}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {questionsRightRailContent === 'chat' ? (
                  <div className="h-full">
                    <ChatInterface
                      ref={chatRef}
                      sessionId={sessionId}
                      patientId={session.patientId}
                      patientName={`${patient.firstName} ${patient.lastName}`}
                      patientData={dashboardData as unknown as Record<string, unknown>}
                      startup={chatStartup}
                startupLoading={startupLoading}
                      suggestedQuestions={[
                        t('chat.suggestedHospitalStay', { name: patient.firstName }),
                        t('chat.suggestedWarningSigns'),
                        t('chat.suggestedRecoveryHelp', { name: patient.firstName }),
                        t('chat.suggestedBreathing'),
                        t('chat.suggestedLongTermEffects'),
                        t('chat.suggestedActivities')
                      ]}
                    />
                  </div>
                ) : (
                  <div className="h-full flex flex-col">
                    <div className="p-4 border-b flex-shrink-0">
                      <h3 className="text-sm font-medium text-text-primary">{t('dashboard.medicalRecordsHeading')}</h3>
                    </div>
                    <div className="flex-1 min-h-0">
                      <RecordsPanel
                        data={dashboardData}
                        startup={startup ?? null}
                        onAskQuestion={handleAskQuestion}
                        variant="rail"
                        onOpenFull={() => setMainView('records')}
                        openSection={openRecordsSection}
                        onOpenSectionChange={setOpenRecordsSection}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}
